import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteTenantAiConfig,
  extractTenantErrorMessage,
  fetchTenantAiConfig,
  isNotFoundError,
  updateTenantAiConfig,
} from '../services/tenantService';
import type { TenantAiConfig, TenantAiConfigUpdate } from '../services/types';

/** Zustand der KI-Konfiguration eines Mandanten samt Aktionen. */
export interface TenantAiConfigState {
  /** Geladene Konfiguration oder null (kein Mandant, laedt noch, Fehler, geloescht). */
  readonly data: TenantAiConfig | null;
  /** true, waehrend die Konfiguration geladen wird. */
  readonly loading: boolean;
  /** true, waehrend eine Aenderung gespeichert wird. */
  readonly saving: boolean;
  /** true, waehrend die Konfiguration geloescht wird. */
  readonly deleting: boolean;
  /** Deutsche Fehlermeldung des letzten fehlgeschlagenen Vorgangs oder null. */
  readonly error: string | null;
  /** Deutsche Erfolgsmeldung des letzten Speicher-/Loeschvorgangs oder null. */
  readonly successMessage: string | null;
  /** Speichert die Aenderung; uebernimmt bei Erfolg den bestaetigten Stand. */
  save(update: TenantAiConfigUpdate): void;
  /** Loescht Key, Modell und Prompt; leert bei Erfolg die Konfiguration. */
  remove(): void;
  /** Blendet eine noch sichtbare Erfolgs-/Fehlermeldung aus. */
  clearMessages(): void;
}

/** Fehlermeldung fuer fehlgeschlagene Lade-Requests. */
const LOAD_ERROR_MESSAGE = 'Die KI-Konfiguration konnte nicht geladen werden.';

/** Fehlermeldung, wenn der Mandant nicht (mehr) existiert (HTTP 404). */
const NOT_FOUND_MESSAGE = 'Der Mandant wurde nicht gefunden.';

/** Fehlermeldung fuer fehlgeschlagene Speicher-Requests. */
const SAVE_ERROR_MESSAGE = 'Die KI-Konfiguration konnte nicht gespeichert werden.';

/** Fehlermeldung fuer fehlgeschlagene Loesch-Requests. */
const DELETE_ERROR_MESSAGE = 'Die KI-Konfiguration konnte nicht gelöscht werden.';

/** Erfolgsmeldung nach erfolgreichem Speichern. */
const SAVE_SUCCESS_MESSAGE = 'Die KI-Konfiguration wurde gespeichert.';

/** Erfolgsmeldung nach erfolgreichem Loeschen. */
const DELETE_SUCCESS_MESSAGE = 'Die KI-Konfiguration wurde gelöscht.';

/** Geladenes Ergebnis samt zugehoeriger Mandanten-ID (Stale-Guard). */
interface LoadedConfig {
  readonly key: string;
  readonly data: TenantAiConfig | null;
  readonly error: string | null;
}

/**
 * Laedt und pflegt die KI-Konfiguration des gewaehlten Mandanten (kein
 * Caching, Projektstandard). `tenantId === null` bedeutet: kein Mandant
 * gewaehlt — es wird nichts geladen und nichts angezeigt.
 *
 * Stale-Guard wie in useUserDetail: Solange das geladene Ergebnis nicht
 * zur aktuellen ID gehoert, meldet der Hook `loading` und liefert KEINE
 * Daten. Ein laufender Speicher-/Loesch-Request wird beim Mandantenwechsel
 * abgebrochen (AbortController) und dessen verspaetete Antwort ignoriert
 * — sonst koennte sie den neu gewaehlten Mandanten veraendern.
 *
 * save()/remove() uebernehmen ausschliesslich den vom Backend
 * BESTAETIGTEN Stand (kein optimistisches Update): Bei save() ist das die
 * Antwort von PUT .../ai-config (z. B. veraendert sich apiKeyHint
 * serverseitig), bei remove() die lokal konstruierte leere Konfiguration
 * NACH dem erfolgreichen 204 von DELETE .../ai-config.
 */
export function useTenantAiConfig(tenantId: string | null): TenantAiConfigState {
  const [loaded, setLoaded] = useState<LoadedConfig | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const mutationControllerRef = useRef<AbortController | null>(null);

  // Mandantenwechsel: Ladezustand zuruecksetzen und Meldungen verwerfen
  // (gehoeren zum vorherigen Mandanten).
  //
  // Die Cleanup-Funktion (nicht der Effekt-Beginn!) bricht sowohl den GET-
  // als auch einen noch laufenden Mutations-Request (save()/remove()) ab
  // und setzt die Ref zurueck. Das deckt zwei Faelle ab, die ein Abbruch
  // NUR am Effekt-Beginn nicht abdeckt: Unmount waehrend einer laufenden
  // Mutation (dann laeuft der Effekt kein zweites Mal, "nur am Beginn"
  // griffe also nie) sowie Race-Schutz beim schnellen Wechsel A -> B -> A:
  // TenantSelect ist zwar waehrend saving/deleting deaktiviert (siehe
  // AiConfigForm/AiConfigPage), diese Abbruch-Logik bleibt aber als
  // zweite Verteidigungslinie bestehen, falls die Sperre je umgangen wird
  // — ein clientseitig abgebrochener PUT/DELETE darf auf keinen Fall
  // still eine verspaetete Antwort fuer den falschen Mandanten anwenden.
  useEffect(() => {
    setLoaded(null);
    setSaving(false);
    setDeleting(false);
    setSuccessMessage(null);
    setMutationError(null);

    if (tenantId === null) {
      return () => {
        mutationControllerRef.current?.abort();
        mutationControllerRef.current = null;
      };
    }
    const controller = new AbortController();
    fetchTenantAiConfig(tenantId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({ key: tenantId, data: response, error: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setLoaded({
          key: tenantId,
          data: null,
          error: isNotFoundError(error) ? NOT_FOUND_MESSAGE : LOAD_ERROR_MESSAGE,
        });
      });
    return () => {
      controller.abort();
      mutationControllerRef.current?.abort();
      mutationControllerRef.current = null;
    };
  }, [tenantId]);

  const save = useCallback(
    (update: TenantAiConfigUpdate): void => {
      if (tenantId === null || saving || deleting) {
        return;
      }
      mutationControllerRef.current?.abort();
      const controller = new AbortController();
      mutationControllerRef.current = controller;
      setSaving(true);
      setMutationError(null);
      setSuccessMessage(null);
      updateTenantAiConfig(tenantId, update, controller.signal)
        .then((confirmed) => {
          if (controller.signal.aborted) {
            return;
          }
          setSaving(false);
          setLoaded({ key: tenantId, data: confirmed, error: null });
          setSuccessMessage(SAVE_SUCCESS_MESSAGE);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          setSaving(false);
          setMutationError(
            isNotFoundError(error)
              ? NOT_FOUND_MESSAGE
              : extractTenantErrorMessage(error, SAVE_ERROR_MESSAGE)
          );
        });
    },
    [tenantId, saving, deleting]
  );

  const remove = useCallback((): void => {
    if (tenantId === null || saving || deleting) {
      return;
    }
    mutationControllerRef.current?.abort();
    const controller = new AbortController();
    mutationControllerRef.current = controller;
    setDeleting(true);
    setMutationError(null);
    setSuccessMessage(null);
    deleteTenantAiConfig(tenantId, controller.signal)
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }
        setDeleting(false);
        setLoaded({
          key: tenantId,
          data: {
            tenantId,
            model: null,
            apiKeyConfigured: false,
            apiKeyHint: null,
            locationPrompt: null,
            reasoningEffort: null,
            webSearchMaxResults: null,
            updatedAt: null,
          },
          error: null,
        });
        setSuccessMessage(DELETE_SUCCESS_MESSAGE);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setDeleting(false);
        setMutationError(
          isNotFoundError(error)
            ? NOT_FOUND_MESSAGE
            : extractTenantErrorMessage(error, DELETE_ERROR_MESSAGE)
        );
      });
  }, [tenantId, saving, deleting]);

  const clearMessages = useCallback((): void => {
    setSuccessMessage(null);
    setMutationError(null);
  }, []);

  if (tenantId === null) {
    return {
      data: null,
      loading: false,
      saving: false,
      deleting: false,
      error: null,
      successMessage: null,
      save,
      remove,
      clearMessages,
    };
  }
  const isCurrent = loaded !== null && loaded.key === tenantId;
  return {
    data: isCurrent ? loaded.data : null,
    loading: !isCurrent,
    saving,
    deleting,
    error: isCurrent ? (mutationError ?? loaded.error) : null,
    successMessage: isCurrent ? successMessage : null,
    save,
    remove,
    clearMessages,
  };
}

export default useTenantAiConfig;
